import Rnd from 'react-rnd';
import _ from 'lodash';
import DOMPurify from 'dompurify';
import moment from 'moment-timezone';
import React from 'react';
import PatientUtils from './PatientUtils';
import ClinicUtils from './ClinicUtils';
import CalendarUtils from './CalendarUtils';
import PhysicianUtils from './PhysicianUtils';
import Icon from 'components/Utils/NewIcon';
import CPPUtils from './CPPUtils';

class DocumentTemplateUtils {
  // Memoized: render paths re-run on every keystroke and the SVG strings
  // (fabric.js toSVG output) are immutable, so each drawing is sanitized once.
  sanitizeSvg = _.memoize((svg) =>
    DOMPurify.sanitize(svg, { USE_PROFILES: { svg: true, svgFilters: true } }),
  );

  parseStyle(style) {
    if (typeof style === 'string') {
      try {
        return JSON.parse(style);
      } catch (err) {
        return {};
      }
    }
  }

  convertPrefilledValues(prefilledValues = []) {
    return prefilledValues.reduce((result, val) => {
      result[val.id] = val.value;
      return result;
    }, {});
  }

  getSignatureError() {
    return <span className="error">Error: Please upload a valid signature before proceeding</span>;
  }

  getPlaceholderLabel(placeholder) {
    return PLACEHOLDERS['text'][placeholder] || PLACEHOLDERS['checkbox'][placeholder];
  }

  getPlaceholderList(type) {
    let list = [];

    let index = undefined;
    if (type === 'textarea' || type === 'textfield') index = 'text';
    else if (type === 'checkbox') index = 'checkbox';

    for (let key in PLACEHOLDERS[index]) {
      let text = PLACEHOLDERS[index][key];
      list.push({ value: key, label: text });
    }
    return list;
  }

  addPercentages(a, b) {
    a = a.split('%');
    a = parseFloat(a[0]);
    b = b.split('%');
    b = parseFloat(b[0]);
    return a + b + '%';
  }

  percentToPixel(percent = '1%', max) {
    percent = percent.split('%');
    percent = parseFloat(percent[0]);
    percent = (max / 100.0) * percent;
    return percent;
  }

  percent(val, max) {
    return (val / max) * 100.0 + '%';
  }

  getBasicElement(id, content, posSize, style = {}) {
    let { x, y, w, h } = posSize;
    return (
      <div
        className="document-template-element"
        style={{
          width: w + 'px',
          height: h + 'px',
          top: y + 'px',
          left: x + 'px',
          position: 'absolute',
          ...style,
        }}
      >
        {content}
      </div>
    );
  }

  getRndElement(id, content, posSize, editMode, onDragStart, onDragStop, onResize, style) {
    if (!editMode) return this.getBasicElement(id, content, posSize, style);

    let { x, y, w, h } = posSize;
    return (
      <Rnd
        key={id + '-' + x + '-' + y}
        style={style}
        size={{ width: w, height: h }}
        onClick={(e) => console.log(e)}
        position={{ x, y }}
        disableDragging={!editMode}
        onDragStart={(ev) => {
          onDragStart(id, ev);
        }}
        enableResizing={editMode && onResize ? { bottomRight: true, bottom: true, top: true } : {}}
        onDragStop={(event, dragObject) => {
          if (onDragStop) onDragStop(id, dragObject.lastX, dragObject.lastY);
        }}
        onResizeStart={(e, direction, ref, delta, position) => {
          onDragStart(id, e);
        }}
        onResize={(e, direction, ref, delta, position) => {
          if (onResize) onResize(id, { w: ref.offsetWidth, h: ref.offsetHeight });
        }}
      >
        {content}
      </Rnd>
    );
  }

  getPrefill(
    patient,
    physician,
    clinic,
    lifelabContract,
    prefilledValues = {},
    hrSummary,
    healthRecords,
  ) {
    let data = { ...prefilledValues };

    let formatYMD = 'YYYY/MM/DD';

    data.checked_by_default = true;

    if (patient) {
      if (patient.address) {
        data.patient_address = PatientUtils.getAddress(patient);
        if (patient.address.line1) {
          data.patient_address_line1 = patient.address.line1;
          data.patient_address_line1_2 =
            patient.address.line1 + ' ' + (patient.address.line2 || '');
        }
        if (patient.address.line2) data.patient_address_line2 = patient.address.line2;
        if (patient.address.postcode) data.patient_address_postcode = patient.address.postcode;
        if (patient.address.city) data.patient_address_city = patient.address.city;
        if (patient.address.state) data.patient_address_state = patient.address.state;
        if (patient.address.country) data.patient_address_country = patient.address.country;
      }

      if (patient.phone) data.patient_phone = patient.phone;
      if (patient.email) data.patient_email = patient.email;
      if (patient.first_name) data.patient_first_name = patient.first_name;
      if (patient.second_name) data.patient_middle_name = patient.second_name;
      if (patient.last_name) data.patient_last_name = patient.last_name;
      if (patient.gender) data.patient_gender = patient.gender;

      /**@type {import('../generated/api').HealthCard}*/
      let health_card = patient.health_card;

      let gender = PatientUtils.getGender(patient);
      if (gender) data.patient_gender = gender;
      if (gender === 'Male') data.patient_gender_male = true;
      if (gender === 'Female') data.patient_gender_female = true;

      data.patient_first_name = patient.first_name;
      data.patient_middle_name = patient.second_name;
      data.patient_last_name = patient.last_name;
      data.patient_emergency_contact_name = patient.emergency_contact?.name;
      data.patient_emergency_contact_phone = patient.emergency_contact?.contact;

      data.patient_name = PatientUtils.getName(patient, '', false, true);

      if (patient.date_of_birth) {
        data.patient_birthdate = CalendarUtils.format(patient.date_of_birth, formatYMD);
        data.patient_birthdate_ddmmyy = PatientUtils.getBirthdate(patient, 'DD/MM/YY', false);
        data.patient_birthdate_ddmmyyyy = PatientUtils.getBirthdate(patient, 'DD/MM/YYYY', false);
        data.patient_birthdate_day = PatientUtils.getBirthdate(patient, 'DD', false);
        data.patient_birthdate_month = PatientUtils.getBirthdate(patient, 'MM', false);
        data.patient_birthdate_year = PatientUtils.getBirthdate(patient, 'YYYY', false);
        data.patient_age = `${PatientUtils.getAgeAsNumber(patient)}`;
        data.patient_age_months = `${moment().diff(moment.unix(patient.date_of_birth), 'M')}`;
      }

      if (health_card) {
        data.patient_ohip_valid = health_card.valid_card === true;
        data.patient_no_ohip_valid = !data.patient_ohip_valid;

        if (health_card.health_number) {
          data.patient_ohip_number = health_card.health_number;
          data.patient_ohip_number_only = health_card.health_number;
          data.patient_ohip_number_formatted = ohipFormat(health_card.health_number);

          let separatedOhip = separateOhip(health_card.health_number);

          data.patient_ohip_number_separated_first = separatedOhip[0];
          data.patient_ohip_number_separated_second = separatedOhip[1];
          data.patient_ohip_number_separated_third = separatedOhip[2];
        }

        if (health_card.version_code) {
          data.patient_ohip_number += health_card.version_code;
          data.patient_ohip_version_only = health_card.version_code;
        }

        if (health_card.expiry_date) {
          data.patient_ohip_expiry = CalendarUtils.format(health_card.expiry_date, 'YYYY/MM/DD');
        }
      }
    }

    if (healthRecords?.allergies) {
      data.patient_allergies =
        healthRecords.allergies
          .filter((it) => CPPUtils.isHealthRecordItemEnabled(it))
          .map((it) => it.value.name)
          .join(', ') || 'NKA';
    }

    if (hrSummary) {
      const weight = hrSummary?.find((it) => it.metric?.id === 'weight');
      if (weight && weight.last_value && weight.metric?.units) {
        data.patient_weight = `${weight.last_value.value1} ${weight.metric.units}`;
        data.patient_weight_date = moment.unix(weight.last_value.date).format(formatYMD);
      }
      const height = hrSummary?.find((it) => it.metric?.id === 'height');
      if (height && height.last_value && height.metric?.units) {
        data.patient_height = `${height.last_value.value1} ${height.metric.units}`;
        data.patient_height_date = moment.unix(height.last_value.date).format(formatYMD);
      }
    }

    if (physician) {
      if (physician.first_name) {
        data.practitioner_name = PhysicianUtils.getName(physician);
        data.practitioner_initials = PhysicianUtils.getInitials(physician);
      }

      data.practitioner_first_name = physician.first_name;
      data.practitioner_last_name = physician.last_name;

      data.practitioner_billing = physician.billing_number;
      data.practitioner_title = physician.title;
      data.practitioner_college_registration = physician.college_registration;
    }

    if (clinic) {
      data.practitioner_phone = ClinicUtils.getPhone(clinic);
      data.practitioner_fax = ClinicUtils.getFax(clinic);
      data.practitioner_address = ClinicUtils.getAddress(clinic);
      data.practitioner_email = ClinicUtils.getEmail(clinic);
      data.practitioner_clinic = clinic.name;

      let address = ClinicUtils.getAddressAsObject(clinic);
      data.practitioner_address_line1 = address.line1;
      data.practitioner_address_line1_2 = address.line1 + ' ' + (address.line2 || '');
      data.practitioner_address_line2 = address.line2;
      data.practitioner_address_postcode = address.postcode;
      data.practitioner_address_city = address.city;
      data.practitioner_address_state = address.state;
      data.practitioner_address_country = address.country;
    }

    let date = moment();

    data.date = date.tz(CalendarUtils.getTargetTimezone()).format(formatYMD);
    data.date_no_separator = date.tz(CalendarUtils.getTargetTimezone()).format('YYYY  MM  DD');
    data.date_mmddyyyy = date.tz(CalendarUtils.getTargetTimezone()).format('MM/DD/YYYY');
    data.date_mmddyyyy_no_separator = date
      .tz(CalendarUtils.getTargetTimezone())
      .format('MM  DD  YYYY');
    data.date_ddmmyyyy = date.tz(CalendarUtils.getTargetTimezone()).format('DD/MM/YYYY');
    data.date_ddmmyyyy_no_separator = date
      .tz(CalendarUtils.getTargetTimezone())
      .format('DD  MM  YYYY');

    data.date_empty = formatYMD;
    data.date_year_empty_yy = 'YY';
    data.date_year_empty_yyyy = 'YYYY';
    data.date_month_empty = 'MM';
    data.date_day_empty = 'DD';

    data.time = date.tz(CalendarUtils.getTargetTimezone()).format('h:mm A');
    data.time_military = date.tz(CalendarUtils.getTargetTimezone()).format('HH:mm');
    data.date_day = date.tz(CalendarUtils.getTargetTimezone()).format('DD');
    data.date_month = date.tz(CalendarUtils.getTargetTimezone()).format('MM');
    data.date_year_yy = date.tz(CalendarUtils.getTargetTimezone()).format('YY');
    data.date_year_yyyy = date.tz(CalendarUtils.getTargetTimezone()).format('YYYY');

    data.lifelabs_contract = lifelabContract;
    return data;
  }

  getDummyPatient() {
    return {
      health_card: {
        date_of_birth: moment().add(-25, 'years').unix(),
        first_name: 'FirstName',
        second_name: 'SecondName',
        last_name: 'LastName',
        gender: 'Male',
        health_number: '1234567890',
        version_code: 'BB',
        valid_card: true,
      },
      date_of_birth: moment().add(-25, 'years').unix(),
      first_name: 'FirstName',
      second_name: 'SecondName',
      last_name: 'LastName',
      emergency_contact: {
        name: 'EmergencyContactName',
        contact: '+16479514770',
      },
      gender: 'Male',
      address: {
        line1: 'line1',
        line2: 'line2',
        postcode: 'AAAAAA',
        city: 'Toronto',
        state: 'Ontario',
        country: 'Canada',
      },
      phone: '+16479514770',
      phone2: '+16479514770',
      email: 'test@careand.ca',
    };
  }

  getDummyPhysician() {
    return {
      first_name: 'FirstName',
      last_name: 'LastName',
      billing_number: 'BillingNumber',
      title: 'Title',
      college_registration: 'Registration#',
    };
  }

  getInputTypes() {
    return INPUT_TYPES;
  }

  getSmartInputTypes() {
    return SMART_INPUT_TYPES;
  }
}

function ohipFormat(number) {
  return separateOhip(number).join('-');
}

function separateOhip(number) {
  return [number.substring(0, 3), number.substring(3, 5), number.substring(5, 9)];
}

export default new DocumentTemplateUtils();

const PLACEHOLDERS = {
  text: {
    empty: 'Empty',
    patient_name: 'Patient Full Name',
    patient_first_name: 'Patient First Name',
    patient_middle_name: 'Patient Middle Name',
    patient_last_name: 'Patient Last Name',
    patient_address: 'Patient Full Address',
    patient_address_line1: 'Patient Address Line 1',
    patient_address_line1_2: 'Patient Address Line 1+2',
    patient_address_line2: 'Patient Address Line 2',
    patient_address_postcode: 'Patient Address Postcode',
    patient_address_city: 'Patient Address City',
    patient_address_state: 'Patient Address State',
    patient_address_country: 'Patient Address Country',
    patient_gender: 'Patient Gender',
    patient_age: 'Patient Age',
    patient_age_months: 'Patient Age (months)',
    patient_birthdate: 'Patient Birthdate (YYYY/MM/DD)',
    patient_birthdate_ddmmyy: 'Patient Birthdate (DD/MM/YY)',
    patient_birthdate_ddmmyyyy: 'Patient Birthdate (DD/MM/YYYY)',
    patient_birthdate_day: 'Patient Birthdate Day',
    patient_birthdate_month: 'Patient Birthdate Month',
    patient_birthdate_year: 'Patient Birthdate Year',
    patient_phone: 'Patient Phone',
    patient_email: 'Patient Email',
    patient_ohip_number: 'Patient OHIP (NNNNNNNNNNVV)',
    patient_ohip_number_only: 'Patient OHIP Number only',
    patient_ohip_version_only: 'Patient OHIP Version only',
    patient_ohip_number_formatted: 'Patient OHIP (NNNN-NNN-NNN)',
    patient_ohip_number_separated_first: 'Patient OHIP separated (1st part)',
    patient_ohip_number_separated_second: 'Patient OHIP separated (2nd part)',
    patient_ohip_number_separated_third: 'Patient OHIP separated (3rd part)',
    patient_ohip_expiry: 'Patient OHIP expiry (YYYY/MM/DD)',
    patient_emergency_contact_name: 'Patient Emergency Contact Name',
    patient_emergency_contact_phone: 'Patient Emergency Contact Phone',
    practitioner_initials: 'Practitioner Initials',
    practitioner_name: 'Practitioner Full Name',
    practitioner_first_name: 'Practitioner First Name',
    practitioner_last_name: 'Practitioner Last Name',
    practitioner_billing: 'Practitioner Billing #',
    practitioner_college_registration: 'Practitioner College Registration #',
    practitioner_title: 'Practitioner Title',
    practitioner_phone: 'Practitioner Phone',
    practitioner_clinic: 'Practitioner Clinic Name',
    practitioner_fax: 'Practitioner Fax',
    practitioner_address: 'Practitioner Address',
    practitioner_address_line1: 'Practitioner Address Line 1',
    practitioner_address_line1_2: 'Practitioner Address Line 1+2',
    practitioner_address_line2: 'Practitioner Address Line 2',
    practitioner_address_postcode: 'Practitioner Address Postcode',
    practitioner_address_city: 'Practitioner Address City',
    practitioner_address_state: 'Practitioner Address State',
    practitioner_address_country: 'Practitioner Address Country',
    practitioner_email: 'Practitioner Email',
    lifelabs_contract: 'LifeLabs Contract #',
    date: 'Current Date (YYYY/MM/DD)',
    date_no_separator: 'Current Date (YYYY MM DD)',
    date_ddmmyyyy: 'Current Date (DD/MM/YYYY)',
    date_ddmmyyyy_no_separator: 'Current Date (DD MM YYYY)',
    date_mmddyyyy: 'Current Date (MM/DD/YYYY)',
    date_mmddyyyy_no_separator: 'Current Date (MM DD YYYY)',
    time: 'Current Time (h:mm A)',
    time_military: 'Current Time (Military Time)',
    date_day: 'Current Date (DD)',
    date_month: 'Current Date (MM)',
    date_year_yy: 'Current Date (YY)',
    date_year_yyyy: 'Current Date (YYYY)',
    date_empty: 'Empty Date (YYYY/MM/DD)',
    date_year_empty_yyyy: 'Empty Year (YYYY)',
    date_year_empty_yy: 'Empty Year (YY)',
    date_month_empty: 'Empty Month (MM)',
    date_day_empty: 'Empty Day (DD)',
    patient_weight: 'Patient Weight',
    patient_height: 'Patient Height',
    patient_weight_date: 'Patient Weight Date',
    patient_height_date: 'Patient Height Date',
    patient_allergies: 'Patient Allergies',
  },
  checkbox: {
    checked_by_default: 'Checked by default',
    patient_gender_male: 'Patient is Male?',
    patient_gender_female: 'Patient is Female?',
    patient_ohip_valid: 'Valid OHIP',
    patient_no_ohip_valid: 'No Valid OHIP',
  },
};

export const INPUT_TYPES = [
  'textfield',
  'textarea',
  'checkbox',
  'signature',
  'patient signature',
  'stamp',
  'patient stamp',
  'smart',
];
export const SMART_INPUT_TYPES = [
  'growth_chart',
  'bmi',
  'fees',
  'metric',
  'checkbox_sum',
  'formula',
  'other',
];

export const HealthRecordsShortcuts = ({ showHelp = true, titleStyle = {} }) => {
  return (
    <>
      <span className="small-title mb-2">
        <span style={titleStyle}>Health Records Shortcuts</span>
        {showHelp && (
          <Icon
            style={{ display: 'inline-block', margin: '0 15px 0 5px' }}
            src="circle-info"
            alt="These shortcuts will insert specific patient health records into a text field if you type them"
            showTooltip={true}
            tooltipPlace="top"
            width="15px"
          />
        )}
      </span>
      <ul>
        <li>
          <b>T#</b> (Current time)
        </li>
        <li>
          <b>D#</b> (Today or date to post)
        </li>
        <li>&nbsp;</li>
        <li>
          <b>H#</b> (Height)
        </li>
        <li>
          <b>W#</b> (Weight)
        </li>
        <li>
          <b>A#</b> (Allergies)
        </li>
        <li>
          <b>M#</b> (Medications)
        </li>
      </ul>
    </>
  );
};
